/**
 * v6.32.96 — Content Studio (Preview).
 *
 * Strictly READ-ONLY visibility surface. No mutations, no R2 writes,
 * no PDF regen. Surfaces every grade × modality assessment in one
 * place with inline data-quality flags so the operator can decide
 * what to fix before we build the editing surface (Phases B/C/D).
 *
 * The explicit "PREVIEW · Read-only · No writes" banner stays on
 * every screen as a safety contract with the operator.
 */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertTriangle, CheckCircle, Eye, FileText, ImageIcon, Layers,
  Loader2, RefreshCw, ShieldAlert, Pencil, Save, X, Upload, Trash2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MODALITY_LABEL = {
  listening: 'Listening',
  reading: 'Reading',
  speaking: 'Speaking',
  writing: 'Writing',
};

const GRADE_LABEL = {
  K: 'Kindergarten',
  1: 'Grade 1',
  2: 'Grade 2',
  '3-4': 'Grades 3–4',
  '5-6': 'Grades 5–6',
  '7-8': 'Grades 7–8',
  '9-12': 'Grades 9–12',
};

const FLAG_LABEL = {
  empty_question_text: 'Empty question text',
  insufficient_options: 'Fewer than 2 options',
  empty_option: 'Has empty option',
  duplicate_options: 'Duplicate options',
  correct_answer_not_in_options: 'Correct answer is not in options',
  missing_bp_image_url: 'Missing big-picture image URL',
  bp_image_too_small: 'Big-picture image suspiciously small',
  bp_image_file_missing: 'Big-picture image file missing on disk',
  bp_image_unreadable: 'Big-picture image file unreadable',
  bp_image_unknown_url_scheme: 'Big-picture image URL has an unknown scheme',
};

const FLAG_SEVERITY = {
  empty_question_text: 'critical',
  insufficient_options: 'critical',
  correct_answer_not_in_options: 'critical',
  bp_image_file_missing: 'critical',
  empty_option: 'high',
  duplicate_options: 'high',
  missing_bp_image_url: 'high',
  bp_image_too_small: 'medium',
  bp_image_unreadable: 'medium',
  bp_image_unknown_url_scheme: 'medium',
};

const SEVERITY_COLOR = {
  critical: 'bg-rose-100 text-rose-800 border-rose-300',
  high: 'bg-amber-100 text-amber-800 border-amber-300',
  medium: 'bg-sky-100 text-sky-800 border-sky-300',
};

const PreviewBanner = ({ editMode }) => (
  <div
    className={`border-2 border-dashed px-4 py-3 rounded-md flex items-start gap-3 ${
      editMode
        ? 'border-indigo-400 bg-indigo-50 text-indigo-900'
        : 'border-amber-400 bg-amber-50 text-amber-900'
    }`}
    data-testid="content-studio-preview-banner"
  >
    <ShieldAlert size={20} className="mt-0.5 flex-shrink-0" />
    <div className="text-sm leading-snug">
      <div className="font-semibold mb-1">
        {editMode
          ? 'EDIT MODE · Drafts only · Live exam unaffected'
          : 'PREVIEW · Read-only · No writes'}
      </div>
      {editMode ? (
        <>
          Draft edits write ONLY to the new <code className="px-1 bg-white/60 rounded">assessment_drafts</code> collection
          and a staging directory. The live <code className="px-1 bg-white/60 rounded">questions</code> collection,
          R2, BLM PDFs, Print-Shop, and Master Archive are NOT touched until Phase C "Publish" ships
          (separate operator-approved release). Save freely; nothing here can affect students or teachers.
        </>
      ) : (
        <>
          Content Studio is a visibility-only surface in this view (Phase A). It reads from the live
          {' '}<code className="px-1 bg-white/60 rounded">questions</code> collection but performs zero database,
          R2, or PDF writes. Switch to <strong>Edit Drafts</strong> to make changes against
          the staging collection.
        </>
      )}
    </div>
  </div>
);

const FlagPill = ({ flag }) => {
  const severity = FLAG_SEVERITY[flag] || 'medium';
  const color = SEVERITY_COLOR[severity];
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded border ${color} mr-1.5 mb-1 inline-block`}
      data-testid={`content-studio-flag-${flag}`}
      title={`${severity.toUpperCase()} — ${FLAG_LABEL[flag] || flag}`}
    >
      {FLAG_LABEL[flag] || flag}
    </span>
  );
};

const QuestionEditorPanel = ({ q, draft, onSaved, onCancel, auth, loadDrafts }) => {
  const [text, setText] = useState(draft?.draft_question_text ?? q.question_text ?? '');
  const [context, setContext] = useState(
    draft?.draft_big_picture_context ?? q.big_picture_context ?? ''
  );
  const initOptions = draft?.draft_options ?? q.options ?? [];
  const [options, setOptions] = useState([0, 1, 2, 3].map((i) => initOptions[i] || ''));
  const [correct, setCorrect] = useState(draft?.draft_correct_answer ?? q.correct_answer ?? '');
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [stagedImageReloadKey, setStagedImageReloadKey] = useState(0);
  const fileInputRef = useRef(null);

  const handleImagePick = (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image exceeds 8 MB cap.');
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const ensureDraft = async () => {
    if (draft?.draft_id) return draft;
    const r = await axios.post(
      `${API}/api/superadmin/content-studio/drafts`,
      {
        question_id: q.id,
        grade_level: q.grade_level,
        modality: q.modality,
        wida_level: q.wida_level,
        question_number: q.question_number,
      },
      auth()
    );
    return r.data?.draft;
  };

  const handleSave = async () => {
    if (!q.id) {
      toast.error('Question is missing an id; cannot save draft.');
      return;
    }
    setSaving(true);
    try {
      const d = await ensureDraft();
      // Update fields.
      await axios.put(
        `${API}/api/superadmin/content-studio/drafts/${d.draft_id}`,
        {
          draft_question_text: text,
          draft_big_picture_context: context,
          draft_options: options,
          draft_correct_answer: correct,
        },
        auth()
      );
      // Upload image if user picked one.
      if (imageFile) {
        const fd = new FormData();
        fd.append('file', imageFile);
        await axios.post(
          `${API}/api/superadmin/content-studio/drafts/${d.draft_id}/image`,
          fd,
          { ...auth(), headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' } }
        );
        setStagedImageReloadKey((k) => k + 1);
        setImageFile(null);
        setImagePreview(null);
      }
      toast.success('Draft saved · Live exam unaffected');
      await loadDrafts?.();
      onSaved?.();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = async () => {
    if (!draft?.draft_id) {
      onCancel?.();
      return;
    }
    if (!window.confirm('Discard this draft? The staged image will be removed. The live exam is unaffected either way.')) return;
    try {
      await axios.delete(
        `${API}/api/superadmin/content-studio/drafts/${draft.draft_id}`,
        auth()
      );
      toast.success('Draft discarded');
      await loadDrafts?.();
      onCancel?.();
    } catch (e) {
      toast.error(`Discard failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const stagedImageUrl =
    draft?.draft_id && draft?.draft_image_staging
      ? `${API}/api/superadmin/content-studio/drafts/${draft.draft_id}/image?_=${stagedImageReloadKey}`
      : null;

  return (
    <div
      className="mt-3 border-t border-indigo-200 pt-3 bg-indigo-50/40 -mx-4 -mb-4 px-4 pb-4 rounded-b"
      data-testid={`content-studio-editor-${q.id}`}
    >
      <div className="text-xs font-semibold text-indigo-900 mb-2 uppercase tracking-wide">
        Edit draft · live exam unaffected until Phase C Publish
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-1">
          <div className="text-xs font-semibold text-slate-600 mb-1">Big-picture image (draft)</div>
          {imagePreview ? (
            <img src={imagePreview} alt="staged" className="w-full max-h-48 object-contain border-2 border-indigo-300 rounded bg-white" />
          ) : stagedImageUrl ? (
            <img src={stagedImageUrl} alt="staged" className="w-full max-h-48 object-contain border-2 border-indigo-300 rounded bg-white" />
          ) : q.bp_image_url ? (
            <img src={q.bp_image_url} alt="current live" className="w-full max-h-48 object-contain border border-slate-200 rounded bg-slate-50 opacity-60" />
          ) : (
            <div className="border border-dashed border-slate-300 rounded p-4 text-center text-xs text-slate-500">
              No image yet
            </div>
          )}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            ref={fileInputRef}
            onChange={(e) => handleImagePick(e.target.files?.[0])}
            className="hidden"
            data-testid={`content-studio-image-input-${q.id}`}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="w-full mt-2"
            onClick={() => fileInputRef.current?.click()}
            data-testid={`content-studio-image-pick-${q.id}`}
          >
            <Upload size={14} className="mr-1" />
            {stagedImageUrl || imagePreview ? 'Replace image' : 'Upload image'}
          </Button>
          {(imagePreview || stagedImageUrl) && (
            <div className="text-[10px] text-indigo-800 mt-1">
              {imagePreview ? 'Pending upload' : 'Staged'} · saved on Save Draft only
            </div>
          )}
        </div>
        <div className="md:col-span-2 space-y-2">
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Question text</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={2}
              className="w-full text-sm border border-slate-300 rounded px-2 py-1 focus:border-indigo-400 focus:outline-none"
              data-testid={`content-studio-text-${q.id}`}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Big-picture context</label>
            <input
              type="text"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              className="w-full text-sm border border-slate-300 rounded px-2 py-1 focus:border-indigo-400 focus:outline-none"
              data-testid={`content-studio-context-${q.id}`}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Options (radio = correct)</label>
            <div className="space-y-1">
              {options.map((opt, i) => {
                const letter = String.fromCharCode(65 + i);
                return (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`correct-${q.id}`}
                      checked={(correct || '').trim() === (opt || '').trim() && (opt || '').trim() !== ''}
                      onChange={() => setCorrect(opt)}
                      data-testid={`content-studio-correct-${q.id}-${i}`}
                    />
                    <span className="font-mono text-xs text-slate-500 w-5">{letter}.</span>
                    <input
                      type="text"
                      value={opt}
                      onChange={(e) => {
                        const next = [...options];
                        next[i] = e.target.value;
                        setOptions(next);
                        if ((correct || '').trim() === (opt || '').trim()) setCorrect(e.target.value);
                      }}
                      className="flex-1 text-sm border border-slate-300 rounded px-2 py-1 focus:border-indigo-400 focus:outline-none"
                      data-testid={`content-studio-option-${q.id}-${i}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            {draft?.draft_id ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="text-rose-700 hover:bg-rose-50"
                onClick={handleDiscard}
                disabled={saving}
                data-testid={`content-studio-discard-${q.id}`}
              >
                <Trash2 size={14} className="mr-1" /> Discard
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onCancel}
              disabled={saving}
              data-testid={`content-studio-cancel-${q.id}`}
            >
              <X size={14} className="mr-1" /> Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid={`content-studio-save-${q.id}`}
            >
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
              Save Draft
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const QuestionRow = ({ q, idx, editMode, draft, onEditOpen, onEditClose, isEditing, auth, loadDrafts }) => {
  const [expanded, setExpanded] = useState(false);
  const flags = q._flags || [];
  const hasIssues = flags.length > 0;
  const correctIdx =
    Array.isArray(q.options)
      ? q.options.findIndex(
          (opt) => (opt || '').trim() === (q.correct_answer || '').trim()
        )
      : -1;

  return (
    <div
      className={`border rounded-md p-4 ${hasIssues ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200 bg-white'} ${isEditing ? 'ring-2 ring-indigo-300' : ''}`}
      data-testid={`content-studio-question-${q.id || idx}`}
    >
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-mono text-xs text-slate-500">
              Q{q.question_number ?? '?'}
            </span>
            <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
              WIDA {q.wida_level ?? '?'}
            </span>
            {hasIssues ? (
              <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                <AlertTriangle size={12} /> {flags.length} flag{flags.length === 1 ? '' : 's'}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                <CheckCircle size={12} /> clean
              </span>
            )}
            {draft ? (
              <span className="inline-flex items-center gap-1 text-xs text-indigo-700 bg-indigo-100 border border-indigo-200 rounded px-1.5 py-0.5">
                <Pencil size={10} /> Draft saved {new Date(draft.updated_at).toLocaleString()}
              </span>
            ) : null}
          </div>
          <div className="font-medium text-slate-900 mb-1">
            {q.question_text || <em className="text-rose-700">[empty question_text]</em>}
          </div>
          {q.big_picture_context ? (
            <div className="text-xs text-slate-500 italic">
              context: {q.big_picture_context}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`content-studio-toggle-${q.id || idx}`}
          >
            <Eye size={14} className="mr-1" />
            {expanded ? 'Hide' : 'View'}
          </Button>
          {editMode ? (
            <Button
              size="sm"
              variant={isEditing ? 'default' : 'outline'}
              className={isEditing ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : ''}
              onClick={() => (isEditing ? onEditClose?.() : onEditOpen?.(q.id))}
              data-testid={`content-studio-edit-${q.id || idx}`}
            >
              <Pencil size={14} className="mr-1" />
              {isEditing ? 'Editing' : 'Edit'}
            </Button>
          ) : null}
        </div>
      </div>

      {hasIssues ? (
        <div className="mt-2">
          {flags.map((f) => (
            <FlagPill key={f} flag={f} />
          ))}
        </div>
      ) : null}

      {expanded && !isEditing ? (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-slate-200 pt-3">
          <div className="md:col-span-1">
            <div className="text-xs font-semibold text-slate-600 mb-1">
              Big-picture image
            </div>
            {q.bp_image_url ? (
              <img
                src={q.bp_image_url}
                alt={q.big_picture_context || 'big picture'}
                className="w-full h-auto max-h-64 object-contain border border-slate-200 rounded bg-slate-50"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <div className="hidden text-xs text-rose-700 mt-1">
              [image failed to load]
            </div>
            <div className="text-[10px] font-mono text-slate-500 mt-1 break-all">
              {q.bp_image_url || '(no bp_image_url)'}
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="text-xs font-semibold text-slate-600 mb-1">
              Options (correct answer in green)
            </div>
            {Array.isArray(q.options) && q.options.length ? (
              <ol className="space-y-1 text-sm">
                {q.options.map((opt, i) => {
                  const isCorrect = i === correctIdx;
                  const letter = String.fromCharCode(65 + i);
                  return (
                    <li
                      key={i}
                      className={`flex items-start gap-2 px-2 py-1 rounded ${
                        isCorrect
                          ? 'bg-emerald-50 border border-emerald-200'
                          : 'bg-slate-50 border border-slate-200'
                      }`}
                    >
                      <span className="font-mono text-xs text-slate-500 w-5">
                        {letter}.
                      </span>
                      <span className="flex-1">
                        {opt || <em className="text-rose-700">[empty]</em>}
                      </span>
                      {isCorrect ? (
                        <CheckCircle size={14} className="text-emerald-600" />
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            ) : (
              <div className="text-sm text-rose-700">[no options]</div>
            )}
            <div className="mt-3 text-xs">
              <span className="font-semibold text-slate-600">id:</span>{' '}
              <span className="font-mono text-slate-500 break-all">
                {q.id || '(none)'}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      {isEditing ? (
        <QuestionEditorPanel
          q={q}
          draft={draft}
          onSaved={() => { /* keep open after save */ }}
          onCancel={onEditClose}
          auth={auth}
          loadDrafts={loadDrafts}
        />
      ) : null}
    </div>
  );
};

const ContentStudioPreview = () => {
  const [assessments, setAssessments] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [loadingQ, setLoadingQ] = useState(false);
  const [filter, setFilter] = useState('all');
  const [editMode, setEditMode] = useState(false);
  const [drafts, setDrafts] = useState({}); // question_id -> draft
  const [editingQuestionId, setEditingQuestionId] = useState(null);

  const auth = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('mosaic_token')}` },
  });

  const loadAssessments = async () => {
    setLoadingList(true);
    try {
      const r = await axios.get(
        `${API}/api/superadmin/content-studio/assessments`,
        auth()
      );
      setAssessments(r.data?.assessments || []);
    } catch (e) {
      toast.error(`Failed to load assessments: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoadingList(false);
    }
  };

  const loadDrafts = async (grade, modality) => {
    if (!grade || !modality) return;
    try {
      const r = await axios.get(
        `${API}/api/superadmin/content-studio/drafts`,
        { params: { grade, modality, status: 'draft' }, ...auth() }
      );
      const map = {};
      (r.data?.drafts || []).forEach((d) => { map[d.question_id] = d; });
      setDrafts(map);
    } catch (e) {
      // silent — not critical for read-only display
    }
  };

  const loadQuestions = async (grade, modality) => {
    setLoadingQ(true);
    setQuestions(null);
    setEditingQuestionId(null);
    try {
      const r = await axios.get(
        `${API}/api/superadmin/content-studio/questions`,
        { params: { grade, modality }, ...auth() }
      );
      setQuestions(r.data || null);
      // Also pull existing drafts for this assessment.
      await loadDrafts(grade, modality);
    } catch (e) {
      toast.error(`Failed to load questions: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoadingQ(false);
    }
  };

  useEffect(() => {
    loadAssessments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredQuestions = useMemo(() => {
    if (!questions?.questions) return [];
    if (filter === 'all') return questions.questions;
    if (filter === 'flagged') {
      return questions.questions.filter((q) => (q._flags || []).length > 0);
    }
    if (filter === 'clean') {
      return questions.questions.filter((q) => (q._flags || []).length === 0);
    }
    return questions.questions;
  }, [questions, filter]);

  return (
    <div className="space-y-5" data-testid="content-studio-preview-tab">
      <PreviewBanner editMode={editMode} />

      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-md px-4 py-2">
        <div className="text-sm text-slate-600">
          <strong>View mode:</strong> {editMode ? 'Edit Drafts' : 'Read-only'}
        </div>
        <Button
          size="sm"
          variant={editMode ? 'default' : 'outline'}
          className={editMode ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : ''}
          onClick={() => {
            setEditMode((v) => !v);
            setEditingQuestionId(null);
          }}
          data-testid="content-studio-toggle-edit-mode"
        >
          <Pencil size={14} className="mr-1" />
          {editMode ? 'Exit Edit Mode' : 'Enter Edit Mode'}
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Layers size={18} /> Assessments
            </h2>
            <p className="text-xs text-slate-500">
              Every grade × modality combination with at least one published question.
              Sample-based quality scan reflects ≤50 sampled questions per row.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={loadAssessments}
            disabled={loadingList}
            data-testid="content-studio-refresh-list-btn"
          >
            {loadingList ? (
              <Loader2 size={14} className="animate-spin mr-1" />
            ) : (
              <RefreshCw size={14} className="mr-1" />
            )}
            Refresh
          </Button>
        </div>

        {loadingList ? (
          <div className="py-8 text-center text-slate-500">Loading…</div>
        ) : assessments.length === 0 ? (
          <div className="py-8 text-center text-slate-500">
            No assessments found.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {assessments.map((a) => {
              const isSel =
                selected && selected.grade === a.grade && selected.modality === a.modality;
              const flaggedRatio =
                a.sample_size > 0
                  ? Math.round((a.sample_flagged / a.sample_size) * 100)
                  : 0;
              return (
                <button
                  key={`${a.grade}-${a.modality}`}
                  className={`text-left border rounded-md p-3 transition ${
                    isSel
                      ? 'border-indigo-400 bg-indigo-50 ring-2 ring-indigo-200'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  onClick={() => {
                    setSelected({ grade: a.grade, modality: a.modality });
                    loadQuestions(a.grade, a.modality);
                    setFilter('all');
                  }}
                  data-testid={`content-studio-assessment-${a.grade}-${a.modality}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-slate-800">
                      {GRADE_LABEL[a.grade] || a.grade} · {MODALITY_LABEL[a.modality] || a.modality}
                    </div>
                    {flaggedRatio > 0 ? (
                      <span className="text-xs text-amber-700 inline-flex items-center gap-1">
                        <AlertTriangle size={12} /> {flaggedRatio}%
                      </span>
                    ) : (
                      <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                        <CheckCircle size={12} /> clean
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {a.question_count} questions · tier A: {a.tier_a_count} · tier BC: {a.tier_bc_count}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selected ? (
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <FileText size={18} />
                {GRADE_LABEL[selected.grade] || selected.grade} ·{' '}
                {MODALITY_LABEL[selected.modality] || selected.modality}
              </h2>
              {questions ? (
                <p className="text-xs text-slate-500">
                  {questions.total} questions ·{' '}
                  {Object.keys(questions.flag_counts || {}).length === 0
                    ? 'no flags'
                    : `${Object.values(questions.flag_counts || {}).reduce((a, b) => a + b, 0)} total flags across ${Object.keys(questions.flag_counts || {}).length} categories`}
                </p>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="text-sm border border-slate-300 rounded px-2 py-1"
                data-testid="content-studio-filter"
              >
                <option value="all">All</option>
                <option value="flagged">Flagged only</option>
                <option value="clean">Clean only</option>
              </select>
              <Button
                size="sm"
                variant="outline"
                onClick={() => loadQuestions(selected.grade, selected.modality)}
                disabled={loadingQ}
                data-testid="content-studio-refresh-questions-btn"
              >
                {loadingQ ? (
                  <Loader2 size={14} className="animate-spin mr-1" />
                ) : (
                  <RefreshCw size={14} className="mr-1" />
                )}
                Refresh
              </Button>
            </div>
          </div>

          {questions && Object.keys(questions.flag_counts || {}).length > 0 ? (
            <div className="mb-3 p-3 rounded bg-amber-50 border border-amber-200">
              <div className="text-xs font-semibold text-amber-900 mb-1 flex items-center gap-1">
                <AlertTriangle size={12} /> Flag summary
              </div>
              <div className="flex flex-wrap">
                {Object.entries(questions.flag_counts).map(([f, n]) => (
                  <span
                    key={f}
                    className={`text-xs px-2 py-0.5 rounded border ${SEVERITY_COLOR[FLAG_SEVERITY[f] || 'medium']} mr-1.5 mb-1`}
                  >
                    {FLAG_LABEL[f] || f}: {n}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {loadingQ ? (
            <div className="py-8 text-center text-slate-500">Loading questions…</div>
          ) : !questions ? (
            <div className="py-8 text-center text-slate-500">
              Pick an assessment above.
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="py-8 text-center text-slate-500">
              No questions match the current filter.
            </div>
          ) : (
            <div className="space-y-2">
              {filteredQuestions.map((q, i) => (
                <QuestionRow
                  key={q.id || i}
                  q={q}
                  idx={i}
                  editMode={editMode}
                  draft={drafts[q.id]}
                  isEditing={editingQuestionId === q.id}
                  onEditOpen={(qid) => setEditingQuestionId(qid)}
                  onEditClose={() => setEditingQuestionId(null)}
                  auth={auth}
                  loadDrafts={() => loadDrafts(selected.grade, selected.modality)}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-md p-6 text-center text-slate-500 text-sm">
          <ImageIcon size={32} className="mx-auto mb-2 text-slate-400" />
          Pick an assessment above to inspect its questions.
        </div>
      )}
    </div>
  );
};

export default ContentStudioPreview;
